import type {SentryTeamName} from '@stripe-internal/connect-embedded-lib';
import {useAnalytics as useObservabilityAnalytics} from '@sail/observability';
import React from 'react';
import type {AnalyticsRecord} from '../data-layer-frame/AnalyticsTypes';
import type {AuthInfo} from './AuthContext/AuthContext';
import {useAuthContext} from './AuthContext/AuthContext';

type AnalyticsDefaultParams = {
  /* eslint-disable camelcase */
  arb_id: string;
  /* eslint-enable camelcase */
};

const getDefaultParams = (
  authInfo: AuthInfo | undefined,
): AnalyticsDefaultParams | undefined => {
  if (!authInfo) return undefined;
  return {
    arb_id: authInfo.analyticsRbEventId,
  };
};

// Referenced code from manage/frontend/src/platform/analytics/analytics.tsx
const getParams = (
  authInfo: AuthInfo | undefined,
  params: AnalyticsRecord | undefined,
  owner?: SentryTeamName,
): AnalyticsRecord | undefined => {
  const defaultParams = getDefaultParams(authInfo);
  if (!defaultParams && !params && !owner) return undefined;
  return {
    ...(defaultParams || {}),
    ...(params || {}),
  };
};

export function useAnalytics() {
  const analytics = useObservabilityAnalytics();
  const {authInfo} = useAuthContext();

  const modifiedAnalytics = React.useMemo(
    () => ({
      action: (id: string, params?: AnalyticsRecord | undefined) =>
        analytics.action(id, getParams(authInfo, params)),
      link: (id: string, params?: AnalyticsRecord | undefined) =>
        analytics.link(id, getParams(authInfo, params)),
      modal: (id: string, params?: AnalyticsRecord | undefined) =>
        analytics.modal(id, getParams(authInfo, params)),
      track: (id: string, params?: AnalyticsRecord | undefined) =>
        analytics.track(id, getParams(authInfo, params)),
      viewed: (id: string, params?: AnalyticsRecord | undefined) =>
        analytics.viewed(id, getParams(authInfo, params)),
    }),
    [analytics, authInfo],
  );

  if (!authInfo) return analytics;
  return modifiedAnalytics;
}
